import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import Box from "@mui/material/Box";

import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import FormHeader from "views/components/formheader";
import FormButtons from "views/components/formbuttons";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { isSpaceValidateTextField } from "@core/utils/Const";
import Loader from "@core/helper/Loader";

const AddDonationType = (props) => {
  const { open, toggle, type, heading, onSubmitDonation, updateData } = props;
  const [check, setCheck] = useState(false);

  const defaultValues = {
    donationtype: "",
    amount: "",
    fix_amount: false,
    description: "",
  };

  const {
    setValue,
    reset,
    control,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToEmptyValues = useCallback(() => {
    reset();
    setCheck(false);
  }, []);

  const onChangeCheck = (e) => {
    setCheck(e.target.checked);
    if (!e.target.checked) {
      setValue("amount", "");
    }
  };
  const resetToStoredValues = useCallback(() => {
    if (type === "edit") {
      const event = {
        donationtype: updateData?.title,
        amount: updateData?.amount,
        fix_amount: updateData?.is_fixed,
        description: updateData?.home_desc,
      };

      setValue("donationtype", event.donationtype || "");
      setValue("amount", event.amount || "");
      setValue("description", event.description || "");
      setCheck(event.fix_amount);
    }
  }, [setValue, type, updateData]);

  useEffect(() => {
    if (type === "edit") {
      resetToStoredValues();
    } else {
      resetToEmptyValues();
    }
  }, [open, resetToStoredValues, resetToEmptyValues, type]);

  const handleClose = () => {
    clearErrors();
    if (type === "add") {
      reset(defaultValues);
    }
    toggle();
  };

  const submitdonation = (data) => {
    Loader.isLoading(true);
    if (type === "add") {
      const payload = {
        title: data.donationtype,
        amount: data.amount,
        isFixed: check,
        desc: data.description,
      };

      APICall("post", payload, EndPoints.addDonationType)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitDonation();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    } else {
      const payload = {
        id: updateData.id,
        title: data.donationtype,
        amount: data.amount,
        isFixed: check,
        desc: data.description,
      };
      APICall("post", payload, EndPoints.updateDonationType)
        .then((response) => {
          const { isError, message } = response.data;
          if (!isError) {
            toast.success(message);
            onSubmitDonation();
            handleClose();
          } else {
            toast.error(message);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          Loader.isLoading(false);
        });
    }
  };

  const onSubmit = (values) => {
    if (check) {
      if (!values.amount) {
        setError("amount");
        return;
      }
    }
    Loader.isLoading(true);
    submitdonation(values);
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 300, sm: 400 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="donationtype"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Donation Type"
                      error={Boolean(errors.donationtype)}
                      placeholder="Enter Donation_type"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />

                {errors.donationtype && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="amount"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      disabled={!check}
                      value={value}
                      onChange={onChange}
                      fullWidth
                      inputProps={{
                        min: "0",
                      }}
                      type="number"
                      label="Amount"
                      error={Boolean(errors.amount)}
                      placeholder="Amount"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />

                {errors.amount && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={6} sx={{ paddingLeft: "1rem" }}>
              <FormControl fullWidth>
                <Controller
                  name="fix_amount"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox checked={check} onChange={onChangeCheck} />
                        }
                        label={
                          <Typography variant="body1" component="span">
                            Fixed Amount
                          </Typography>
                        }
                      />
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: false,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      multiline
                      rows={4}
                      label="Description"
                      error={Boolean(errors.description)}
                      placeholder="Description"
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />

                {errors.description && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-select"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <FormButtons type={type} resetFunction={resetToEmptyValues} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default AddDonationType;
